import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Reveal } from './Reveal';
import { DemoCta } from './CtaButtons';
import { LandingTvMock } from './LandingTvMock';
import { LANDING_HERO } from '../../domain/landingContent';

gsap.registerPlugin(ScrollTrigger);

/** Hero da landing B2B — jukebox na TV, um único CTA. */
export function LandingHero() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const tvRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const placarRef = useRef<HTMLDivElement>(null);
  const musicaRef = useRef<HTMLDivElement>(null);
  const celularRef = useRef<HTMLDivElement>(null);

  // Pin + parallax só em desktop e sem prefers-reduced-motion. Recria no change dos MQs.
  useEffect(() => {
    const reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktopMq = window.matchMedia('(min-width: 1024px)');
    let ctx: gsap.Context | undefined;

    const setup = () => {
      ctx?.revert();
      ctx = undefined;
      if (reduceMq.matches || !desktopMq.matches) return;
      ctx = gsap.context(() => {
        gsap
          .timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top top',
              end: () => `+=${window.innerHeight * 0.9}`,
              scrub: 1,
              pin: true,
              invalidateOnRefresh: true,
            },
          })
          .to(contentRef.current, { autoAlpha: 0, y: -60, scale: 0.94, ease: 'none' }, 0)
          .to(tvRef.current, { scale: 1.14, y: -30, ease: 'none' }, 0)
          .to(glowRef.current, { y: 40, ease: 'none' }, 0)
          .to(placarRef.current, { y: -70, x: 20, ease: 'none' }, 0)
          .to(musicaRef.current, { y: 70, x: -20, ease: 'none' }, 0)
          .to(celularRef.current, { y: 40, x: 16, ease: 'none' }, 0);
      }, sectionRef);
    };

    setup();
    reduceMq.addEventListener('change', setup);
    desktopMq.addEventListener('change', setup);
    return () => {
      reduceMq.removeEventListener('change', setup);
      desktopMq.removeEventListener('change', setup);
      ctx?.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="mx-auto max-w-5xl px-4 pt-12 text-center sm:px-6 sm:pt-20"
    >
      <div ref={contentRef}>
        <Reveal className="flex justify-center">
          <span className="inline-flex max-w-full flex-wrap items-center justify-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-center text-xs font-medium text-orange-300">
            <span className="h-1.5 w-1.5 rounded-full bg-orange-400 animate-pulse-slow" />
            {LANDING_HERO.badge}
          </span>
        </Reveal>

        <Reveal delay={0.08}>
          <h1 className="mx-auto mt-7 max-w-3xl break-words text-balance text-4xl font-black leading-[1.04] tracking-tight text-white sm:text-6xl">
            {LANDING_HERO.headline}
          </h1>
        </Reveal>

        <Reveal delay={0.16}>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
            {LANDING_HERO.subcopy}
          </p>
        </Reveal>

        <Reveal delay={0.24}>
          <div className="mx-auto mt-10 flex w-full max-w-sm flex-col items-stretch gap-3 sm:max-w-none sm:flex-row sm:justify-center">
            <DemoCta width="full" />
          </div>
          <p className="mt-5 text-xs text-slate-500">{LANDING_HERO.microcopy}</p>
        </Reveal>
      </div>

      <Reveal delay={0.3} className="mt-16 sm:mt-20">
        <div ref={tvRef}>
          <LandingTvMock
            glowRef={glowRef}
            placarRef={placarRef}
            musicaRef={musicaRef}
            celularRef={celularRef}
          />
        </div>
      </Reveal>
    </section>
  );
}
